import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { MapIcon } from '@heroicons/react/24/outline';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading.json';
import TypeAhead, { TypeAheadOption } from './TypeAhead';
import { EnrichedDriver } from './DriverDrawer';

// ============================================================================
// TYPES
// ============================================================================

export interface DriverCompactPopupProps {
  /** Whether the popup is open */
  open: boolean;

  /** Callback to close the popup */
  onClose: () => void;

  /** Position of the popup (absolute positioning) — legacy, prefer anchorRef */
  position: { top: number; left: number } | null;

  /** Ref to the anchor element for viewport-aware positioning (flip + scroll tracking) */
  anchorRef?: React.RefObject<HTMLElement | null>;

  /** List of enriched drivers to display */
  drivers: EnrichedDriver[];

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Callback when a driver is selected */
  onSelectDriver: (driver: EnrichedDriver) => void;

  /** Callback when "View on Map" is clicked */
  onOpenMapView: () => void;

  /** Optional initial filter text */
  initialFilterText?: string;

  /** Optional initial dispatcher filter */
  initialDispatcher?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * DriverCompactPopup - Compact popup for quick driver selection
 *
 * Features:
 * - Compact list view of drivers
 * - Search and filter by dispatcher
 * - Shows qualification, distance, availability
 * - Button to open full map view
 * - Positioned absolutely via portal
 *
 * Usage:
 * ```tsx
 * <DriverCompactPopup
 *   open={popupOpen}
 *   onClose={() => setPopupOpen(false)}
 *   position={popupPosition}
 *   drivers={drivers}
 *   loading={loading}
 *   error={error}
 *   onSelectDriver={handleDriverSelect}
 *   onOpenMapView={handleOpenMapView}
 * />
 * ```
 */
export const DriverCompactPopup: React.FC<DriverCompactPopupProps> = ({
  open,
  onClose,
  position,
  anchorRef,
  drivers,
  loading,
  error,
  onSelectDriver,
  onOpenMapView,
  initialFilterText = '',
  initialDispatcher = '',
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [filterText, setFilterText] = useState(initialFilterText);
  const [selectedDispatcher, setSelectedDispatcher] = useState(initialDispatcher);
  const popupRef = useRef<HTMLDivElement>(null);
  const [computedPosition, setComputedPosition] = useState<{ top: number; left: number; openUpward: boolean } | null>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Viewport-aware positioning: flip upward when needed, reposition on scroll/resize.
   * Falls back to the static `position` prop when no anchorRef is provided.
   */
  useEffect(() => {
    if (!open) { setComputedPosition(null); return; }

    const anchor = anchorRef?.current;
    if (!anchor) {
      // Legacy fallback: use static position prop
      if (position) setComputedPosition({ top: position.top, left: position.left, openUpward: false });
      return;
    }

    const POPUP_WIDTH = 384; // w-96
    const POPUP_MAX_HEIGHT = 500;
    const SPACING = 4;

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < POPUP_MAX_HEIGHT && rect.top > spaceBelow;

      setComputedPosition({
        top: openUpward ? rect.top - SPACING : rect.bottom + SPACING,
        left: Math.max(8, rect.right - POPUP_WIDTH),
        openUpward,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, anchorRef, position]);

  /**
   * Close popup when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, onClose]);

  /**
   * Reset filters when popup closes
   */
  useEffect(() => {
    if (!open) {
      setFilterText(initialFilterText);
      setSelectedDispatcher(initialDispatcher);
    }
  }, [open, initialFilterText, initialDispatcher]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get unique dispatchers and convert to TypeAhead options
   */
  const dispatcherOptions: TypeAheadOption[] = [
    { value: '', label: 'All Dispatchers' },
    ...Array.from(new Set(drivers.map(d => d.dispatcher).filter(Boolean)))
      .map(dispatcher => ({
        value: dispatcher as string,
        label: dispatcher as string
      }))
  ];

  /**
   * Filter drivers based on search text and dispatcher
   */
  const filteredDrivers = drivers.filter(driver => {
    const matchesText = !filterText ||
      driver.driverName?.toLowerCase().includes(filterText.toLowerCase()) ||
      driver.unitNumber?.toLowerCase().includes(filterText.toLowerCase());

    const matchesDispatcher = !selectedDispatcher || driver.dispatcher === selectedDispatcher;

    return matchesText && matchesDispatcher;
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!open || !computedPosition) return null;

  return createPortal(
    <div
      ref={popupRef}
      className="fixed w-96 bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[200] flex flex-col"
      style={{
        ...(computedPosition.openUpward
          ? { bottom: `${window.innerHeight - computedPosition.top}px` }
          : { top: `${computedPosition.top}px` }),
        left: `${computedPosition.left}px`,
        maxHeight: '500px',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-t-lg">
        <h3 className="text-sm font-semibold text-heading">
          Select Driver
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenMapView}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="View on Map"
          >
            <MapIcon className="h-5 w-5" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
        {/* Search Input */}
        <input
          type="text"
          placeholder="Search drivers..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Dispatcher Filter with TypeAhead */}
        {dispatcherOptions.length > 1 && (
          <TypeAhead
            options={dispatcherOptions}
            value={selectedDispatcher}
            onChange={setSelectedDispatcher}
            placeholder="All Dispatchers"
            className="text-xs"
            showSearch={false}
            onClear={() => setSelectedDispatcher('')}
          />
        )}
      </div>

      {/* Driver List */}
      <div className="overflow-y-auto flex-1 rounded-b-lg">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Lottie animationData={loadingAnimation} loop={true} style={{ width: '48px', height: '48px' }} />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 px-4 text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="flex items-center justify-center py-8 px-4 text-center">
            <p className="text-sm text-muted">No drivers found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredDrivers.map((driver, index) => (
              <button
                key={`${driver.unitNumber}-${index}`}
                onClick={() => onSelectDriver(driver)}
                className={`w-full px-3 py-3 transition-colors text-left border-l-4 ${
                  index === filteredDrivers.length - 1 ? 'rounded-b-lg' : ''
                } ${
                  driver.isQualified
                    ? 'border-l-green-500 bg-green-50/30 dark:bg-green-900/10 hover:bg-green-50 dark:hover:bg-green-900/20'
                    : 'border-l-red-500 bg-red-50/30 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm text-heading">
                        {driver.unitNumber}
                      </span>
                      {!driver.isQualified && (
                        <span className="px-1.5 py-0.5 text-xs bg-danger text-danger-fg rounded">
                          Not Qualified
                        </span>
                      )}
                      {/* Requirement Icons */}
                      {Array.isArray(driver.attributes) && driver.attributes.includes('daycab') && (
                        <span className="gw-icon gw-icon-daycab text-blue-700" style={{ fontSize: '12px' }} title="Daycab"></span>
                      )}
                      {Array.isArray(driver.attributes) && driver.attributes.includes('hazmat') && (
                        <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '12px' }} title="Hazmat"></span>
                      )}
                      {Array.isArray(driver.attributes) && driver.attributes.includes('tank') && (
                        <span className="gw-icon gw-icon-tank text-purple-600" style={{ fontSize: '12px' }} title="Tank"></span>
                      )}
                      {Array.isArray(driver.attributes) && driver.attributes.includes('93k') && (
                        <span className="gw-icon text-heading" style={{ fontSize: '12px' }} title="93K">&#xE9E5;</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {driver.driverName}
                    </p>
                    {driver.dispatcher && (
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {driver.dispatcher}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {driver.distanceFromOrigin !== null && (
                      <p className={`text-xs font-medium ${
                        driver.distanceFromOrigin < 25
                          ? 'text-green-600 dark:text-green-400'
                          : driver.distanceFromOrigin < 100
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {driver.distanceFromOrigin.toFixed(0)} mi
                      </p>
                    )}
                    {driver.dispatchInfo && (
                      <p className={`text-xs font-medium ${
                        driver.dispatchInfo.isCompleted
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-orange-600 dark:text-orange-400'
                      }`}>
                        {driver.dispatchInfo.isCompleted ? 'Available' : 'On Load'}
                      </p>
                    )}
                  </div>
                </div>
                {driver.dispatchInfo && !driver.dispatchInfo.isCompleted && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 truncate">
                    PRO #{driver.dispatchInfo.proNumber}: {driver.dispatchInfo.originName} → {driver.dispatchInfo.destinationName}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DriverCompactPopup;
